import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { isNotificationUnread, useNotificationsInbox } from '../../context/NotificationsInboxContext';
import { useAuth } from '../../context/AuthContext';
import { notificationService } from '../../services/notificationService';
import { bagService } from '../../services';
import { resolveNotificationNavigationTargetAsync } from '../../utils/notificationNavigation';
import LoadMoreButton from '../common/LoadMoreButton';

const IconBell = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 01-3.46 0" />
  </svg>
);

function formatNotificationTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const NotificationsDropdown = ({ isOpen, onToggle, onClose, mobile = false }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    items,
    unreadCount,
    loading,
    loadingMore,
    hasMore,
    error,
    load,
    loadMore,
    applyMarkRead,
    setError,
  } = useNotificationsInbox();
  const wrapRef = useRef(null);
  const bodyRef = useRef(null);
  const loadMorePendingRef = useRef(false);

  const tryLoadMoreFromScroll = useCallback(() => {
    if (!hasMore || loadMorePendingRef.current) return;
    const el = bodyRef.current;
    if (!el) return;
    const threshold = 48;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
      loadMorePendingRef.current = true;
      Promise.resolve(loadMore()).finally(() => {
        loadMorePendingRef.current = false;
      });
    }
  }, [hasMore, loadMore]);

  useEffect(() => {
    if (!isOpen) return;
    load();
  }, [isOpen, load]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (typeof e.target.closest === 'function' && e.target.closest('.notifications-dropdown')) return;
      onClose();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  const handleRowClick = async (n) => {
    const wasUnread = isNotificationUnread(n);
    if (wasUnread) {
      applyMarkRead(n.id, new Date().toISOString(), true);
    }
    setError('');
    try {
      if (wasUnread) {
        const res = await notificationService.markRead(n.id);
        const serverReadAt = res?.notification?.read_at;
        if (serverReadAt) {
          applyMarkRead(n.id, serverReadAt, false);
        }
      }
    } catch (e) {
      setError(e.message || 'Could not update notification');
      if (wasUnread) {
        load();
      }
    }
    const target = await resolveNotificationNavigationTargetAsync(n, bagService, user?.id);
    onClose();
    const navigateOpts = target.state ? { state: target.state } : {};
    navigate({ pathname: target.pathname, search: target.search }, navigateOpts);
  };

  const panel = isOpen ? (
    <div
      className={`notifications-dropdown ${mobile ? 'notifications-dropdown--mobile' : ''}`}
      role="dialog"
      aria-label="Notifications"
    >
      {error ? <div className="notifications-dropdown__error">{error}</div> : null}
      <div
        className="notifications-dropdown__body"
        ref={bodyRef}
        onScroll={tryLoadMoreFromScroll}
      >
        {loading && items.length === 0 ? (
          <div className="notifications-dropdown__loading">Loading…</div>
        ) : null}
        {!loading && items.length === 0 ? (
          <div className="notifications-dropdown__empty">No notifications yet</div>
        ) : null}
        <ul className="notifications-dropdown__list">
          {items.map((n) => (
            <li key={n.id}>
              <button
                type="button"
                className={`notifications-dropdown__row ${
                  isNotificationUnread(n) ? 'notifications-dropdown__row--unread' : 'notifications-dropdown__row--read'
                }`}
                onClick={() => handleRowClick(n)}
              >
                {isNotificationUnread(n) ? (
                  <span className="notifications-dropdown__unread-dot" aria-hidden />
                ) : null}
                <div className="notifications-dropdown__avatar" aria-hidden>
                  {(n.actor_display_name || '?').slice(0, 2).toUpperCase()}
                </div>
                <div className="notifications-dropdown__text">
                  <p className="notifications-dropdown__line">{n.body}</p>
                  <p className="notifications-dropdown__time">{formatNotificationTime(n.created_at)}</p>
                </div>
              </button>
            </li>
          ))}
        </ul>
        {hasMore && items.length > 0 ? (
          <div className="notifications-dropdown__load-more">
            <LoadMoreButton
              onClick={() => loadMore()}
              disabled={loadingMore}
              loading={loadingMore}
              loadingLabel="Loading…"
            />
          </div>
        ) : null}
      </div>
    </div>
  ) : null;

  return (
    <div className="header-notifications-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`header-bell ${mobile ? 'header-bell--mobile-nav' : 'header-bell--desktop'}`}
        aria-label="Notifications"
        aria-expanded={isOpen}
        onClick={onToggle}
      >
        <IconBell />
        {unreadCount > 0 ? <span className="header-bell__dot" aria-hidden /> : null}
      </button>
      {mobile && panel ? createPortal(panel, document.body) : panel}
    </div>
  );
};

export default NotificationsDropdown;
